/**
 * Recipe Import Page
 *
 * Import recipes from text, URL, or image sources using LLM extraction.
 */

import { Head, usePage } from '@inertiajs/react';
import { useState, useEffect } from 'react';
import type { RecipeImportPageProps, ExtractSourceType, ExtractedRecipe, ExtractedIngredient } from '@/types/recipeImport';
import type { SharedProps } from '@/types';
import { Navbar } from '@/components';
import { SourceInputSection } from '@/components/food/import/SourceInputSection';
import { ExtractionLoadingState } from '@/components/food/import/ExtractionLoadingState';
import { ExtractedRecipePreview } from '@/components/food/import/ExtractedRecipePreview';
import { IngredientMatchingSection } from '@/components/food/import/IngredientMatchingSection';
import { TagSection } from '@/components/food/import/TagSection';
import { ImportActions } from '@/components/food/import/ImportActions';
import { useRecipeExtraction } from '@/hooks/useRecipeExtraction';
import { useRecipeImport } from '@/hooks/useRecipeImport';
import type { ImportRequest, ImportIngredient } from '@/types/recipeImport';

type PageProps = RecipeImportPageProps & SharedProps;

export default function Import() {
  // Get page props (ingredients and tags available for matching)
  const pageProps = usePage<PageProps>().props;

  // Source input state
  const [sourceType, setSourceType] = useState<ExtractSourceType>('text');
  const [textContent, setTextContent] = useState('');
  const [urlContent, setUrlContent] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [pdfFile, setPdfFile] = useState<File | null>(null);

  // Extraction state from hook
  const {
    extractedRecipe,
    extractedIngredients,
    suggestedTags,
    isExtracting,
    extractionError,
    extract,
  } = useRecipeExtraction();

  // Import state from hook
  const { importRecipe, isImporting, importError } = useRecipeImport();

  // Local state for edited recipe data
  const [editedRecipe, setEditedRecipe] = useState<ExtractedRecipe | null>(null);
  const [editedIngredients, setEditedIngredients] = useState<ExtractedIngredient[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);

  // Update edited recipe and ingredients when extraction completes
  useEffect(() => {
    if (extractedRecipe) {
      setEditedRecipe(extractedRecipe);
    }
  }, [extractedRecipe]);

  useEffect(() => {
    if (extractedIngredients) {
      setEditedIngredients(extractedIngredients);
    }
  }, [extractedIngredients]);

  const handleExtract = async () => {
    let content = '';
    let file: File | undefined;

    // Get content based on source type
    if (sourceType === 'text') {
      content = textContent;
    } else if (sourceType === 'url') {
      content = urlContent;
    } else if (sourceType === 'image') {
      file = imageFile || undefined;
    } else if (sourceType === 'pdf') {
      file = pdfFile || undefined;
    }

    // Extract recipe
    await extract(sourceType, content, file);
  };

  /**
   * Build ImportRequest from edited recipe, ingredients, and tags
   */
  const buildImportRequest = (): ImportRequest => {
    if (!editedRecipe) {
      throw new Error('No recipe data to import');
    }

    // Convert ExtractedIngredient[] to ImportIngredient[]
    const ingredients: ImportIngredient[] = editedIngredients.map((ing) => {
      if (ing.is_new) {
        // New ingredient
        return {
          is_new: true,
          name: ing.extracted_name,
          category: ing.suggested_category,
          quantity: ing.quantity,
          unit: ing.unit,
          original_text: ing.original_text,
          canonical_quantity: ing.canonical_quantity,
          canonical_unit: ing.canonical_unit,
          notes: ing.notes,
          optional: ing.optional,
        };
      } else {
        // Existing ingredient
        return {
          ingredient_id: ing.matched_ingredient_id!,
          is_new: false,
          quantity: ing.quantity,
          unit: ing.unit,
          original_text: ing.original_text,
          canonical_quantity: ing.canonical_quantity,
          canonical_unit: ing.canonical_unit,
          notes: ing.notes,
          optional: ing.optional,
        };
      }
    });

    return {
      import: {
        title: editedRecipe.title,
        description: editedRecipe.description,
        instructions: editedRecipe.instructions,
        cooking_time_minutes: editedRecipe.cooking_time_minutes,
        preparation_time_minutes: editedRecipe.preparation_time_minutes,
        servings: editedRecipe.servings,
        calories_per_portion: editedRecipe.calories_per_portion,
        healthiness_rating: editedRecipe.healthiness_rating,
        eat_immediately: editedRecipe.eat_immediately,
        freezable: editedRecipe.freezable,
        fridge_life_days: editedRecipe.fridge_life_days,
        tag_ids: selectedTagIds,
        ingredients,
      },
    };
  };

  /**
   * Handle import submission
   */
  const handleImport = async () => {
    try {
      const importRequest = buildImportRequest();
      await importRecipe(importRequest);
    } catch (error) {
      // Error handled by useRecipeImport hook
    }
  };

  // Check if import is disabled
  const isImportDisabled =
    !editedRecipe ||
    !editedRecipe.title ||
    editedIngredients.length === 0;

  return (
    <>
      <Head title="Import Recipe" />
      <div className="min-h-screen bg-gray-50">
        <Navbar currentSection="food" />
        <div className="max-w-[900px] mx-auto px-5 py-5">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-semibold text-gray-900">Import Recipe</h1>
          </div>

          {/* Source Input Section */}
          <SourceInputSection
            sourceType={sourceType}
            onSourceTypeChange={setSourceType}
            textContent={textContent}
            onTextContentChange={setTextContent}
            urlContent={urlContent}
            onUrlContentChange={setUrlContent}
            imageFile={imageFile}
            onImageFileChange={setImageFile}
            pdfFile={pdfFile}
            onPdfFileChange={setPdfFile}
            onExtract={handleExtract}
            isExtracting={isExtracting}
          />

          {/* Extraction Loading State */}
          {isExtracting && (
            <div className="mt-6">
              <ExtractionLoadingState />
            </div>
          )}

          {/* Extraction Error */}
          {extractionError && !isExtracting && (
            <div className="mt-6 bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <svg
                  className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                  />
                </svg>
                <div>
                  <h3 className="text-sm font-medium text-red-900">Extraction failed</h3>
                  <p className="text-sm text-red-700 mt-1">{extractionError}</p>
                </div>
              </div>
            </div>
          )}

          {/* Extracted Recipe Preview */}
          {editedRecipe && !isExtracting && (
            <div className="mt-6">
              <ExtractedRecipePreview
                recipe={editedRecipe}
                onChange={setEditedRecipe}
              />
            </div>
          )}

          {/* Ingredient Matching Section */}
          {editedRecipe && !isExtracting && editedIngredients.length > 0 && (
            <div className="mt-6">
              <IngredientMatchingSection
                ingredients={editedIngredients}
                availableIngredients={pageProps.ingredients}
                onChange={setEditedIngredients}
              />
            </div>
          )}

          {/* Tag Section */}
          {editedRecipe && !isExtracting && (
            <div className="mt-6">
              <TagSection
                suggestedTags={suggestedTags}
                availableTags={pageProps.tags}
                selectedTagIds={selectedTagIds}
                onChange={setSelectedTagIds}
              />
            </div>
          )}

          {/* Import Error */}
          {importError && (
            <div className="mt-6 bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <svg
                  className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                  />
                </svg>
                <div>
                  <h3 className="text-sm font-medium text-red-900">Import failed</h3>
                  <p className="text-sm text-red-700 mt-1">{importError}</p>
                </div>
              </div>
            </div>
          )}

          {/* Import Actions */}
          {editedRecipe && !isExtracting && (
            <div className="mt-6">
              <ImportActions
                onImport={handleImport}
                isImporting={isImporting}
                isDisabled={isImportDisabled}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
}
